import React, { useState, useEffect, useCallback } from 'react';
import { fetchClients, saveClient, deleteClient, fetchProjectsByClient, generateToken } from '../services/db';
import { saveProject, deleteProject as dbDeleteProject } from '../services/db';

const STATUS_COLORS = {
  'Entwurf':        { dot: 'bg-gray-300',   badge: 'bg-gray-100 text-gray-500' },
  'In Bearbeitung': { dot: 'bg-amber-400',  badge: 'bg-amber-50 text-amber-600' },
  'Bereit':         { dot: 'bg-indigo-500', badge: 'bg-indigo-50 text-indigo-600' },
  'Fertig':         { dot: 'bg-emerald-500',badge: 'bg-emerald-50 text-emerald-700' },
};

function IconFolder({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <path d="M2 5a2 2 0 012-2h3.586a1 1 0 01.707.293L9.707 4.7A1 1 0 0010.414 5H16a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    </svg>
  );
}

function IconPlay({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M3 2L13 8L3 14V2Z" fill="currentColor"/>
    </svg>
  );
}

function IconPlus({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
      <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  );
}

function IconTrash({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <path d="M2 3.5H12M5 3.5V2.5H9V3.5M5.5 6V10.5M8.5 6V10.5M3 3.5L3.5 11.5H10.5L11 3.5H3Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IconArrowLeft({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
      <path d="M10 6H2M5 3L2 6L5 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IconArrowRight({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <path d="M2 7H12M8 3L12 7L8 11" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

export default function Dashboard({ user, onOpen, onAdd, onDelete, planTier, onAgency }) {
  const [clients, setClients]             = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientVideos, setClientVideos]   = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [addingClient, setAddingClient]   = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [addingVideo, setAddingVideo]     = useState(false);
  const [newVideoTitle, setNewVideoTitle] = useState('');
  const [confirmDeleteClient, setConfirmDeleteClient] = useState(null);
  const [confirmDeleteVideo, setConfirmDeleteVideo]   = useState(null);

  const loadClients = useCallback(async () => {
    if (!user) return;
    setLoadingClients(true);
    try {
      const c = await fetchClients(user.uid);
      setClients(c.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0)));
    } finally {
      setLoadingClients(false);
    }
  }, [user]);

  useEffect(() => { loadClients(); }, [loadClients]);

  const loadVideos = useCallback(async (client) => {
    if (!user || !client) return;
    setLoadingVideos(true);
    try {
      const v = await fetchProjectsByClient(user.uid, client.id);
      setClientVideos(v.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0)));
    } finally {
      setLoadingVideos(false);
    }
  }, [user]);

  const openClient = (client) => {
    setSelectedClient(client);
    loadVideos(client);
  };

  const handleAddClient = async () => {
    const name = newClientName.trim() || 'Neuer Kunde';
    const id = await saveClient({ name, ownerId: user.uid });
    setNewClientName('');
    setAddingClient(false);
    await loadClients();
    const newClient = { id, name, ownerId: user.uid };
    openClient(newClient);
  };

  const handleDeleteClient = async (clientId) => {
    await deleteClient(clientId);
    setClients(cs => cs.filter(c => c.id !== clientId));
    setConfirmDeleteClient(null);
  };

  const handleAddVideo = async () => {
    const title = newVideoTitle.trim() || 'Unbenanntes Video';
    const project = {
      shareToken: generateToken(),
      ownerId: user.uid,
      clientId: selectedClient.id,
      title,
      createdAt: new Date().toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' }),
      status: 'Entwurf',
      blocks: [],
      driveLink: '',
    };
    await saveProject(project);
    setNewVideoTitle('');
    setAddingVideo(false);
    onAdd(project);
  };

  const handleDeleteVideo = async (shareToken) => {
    await dbDeleteProject(shareToken);
    setClientVideos(vs => vs.filter(v => v.shareToken !== shareToken));
    setConfirmDeleteVideo(null);
    onDelete(shareToken);
  };

  // ── Client detail view ──
  if (selectedClient) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-10 h-full overflow-y-auto">
        {planTier === 'agency' && (
          <button
            onClick={onAgency}
            className="w-full mb-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-2xl p-4 flex items-center justify-between transition-all shadow-sm group"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 1l2 4h4l-3.5 2.5 1.5 4L8 9.5 4 11.5l1.5-4L2 5h4L8 1z" fill="white"/></svg>
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold">Agentur-Tools</p>
                <p className="text-xs text-white/70">Kampagnenplanung · Social Media · KI-Strategie</p>
              </div>
            </div>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="opacity-70 group-hover:translate-x-0.5 transition-transform"><path d="M3 8H13M9 4L13 8L9 12" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        )}

        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setSelectedClient(null); setClientVideos([]); }}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              <IconArrowLeft />
              Kunden
            </button>
            <span className="text-gray-200">/</span>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-indigo-50 rounded-lg flex items-center justify-center text-indigo-500">
                <IconFolder size={16} />
              </div>
              <h1 className="text-xl font-semibold text-gray-900">{selectedClient.name}</h1>
            </div>
          </div>
          <button
            onClick={() => setAddingVideo(true)}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-xl transition-all shadow-sm"
          >
            <IconPlus />
            Neues Video
          </button>
        </div>

        {addingVideo && (
          <div className="mb-5 bg-white border border-indigo-200 rounded-2xl p-4 flex items-center gap-3 shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-500">
              <IconPlay />
            </div>
            <input
              autoFocus
              value={newVideoTitle}
              onChange={e => setNewVideoTitle(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAddVideo(); if (e.key === 'Escape') { setAddingVideo(false); setNewVideoTitle(''); } }}
              placeholder="Videotitel eingeben..."
              className="flex-1 text-sm font-medium text-gray-800 outline-none placeholder-gray-300"
            />
            <button onClick={handleAddVideo} className="text-xs bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700 font-medium">Erstellen</button>
            <button onClick={() => { setAddingVideo(false); setNewVideoTitle(''); }} className="text-xs text-gray-400 hover:text-gray-600">Abbrechen</button>
          </div>
        )}

        {loadingVideos ? (
          <div className="flex justify-center py-16">
            <svg className="animate-spin w-5 h-5 text-gray-300" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
          </div>
        ) : clientVideos.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-14 h-14 bg-gray-100 rounded-2xl mx-auto mb-4 flex items-center justify-center text-gray-300">
              <IconPlay size={22} />
            </div>
            <p className="text-gray-400 text-sm">Noch keine Videos</p>
            <button onClick={() => setAddingVideo(true)} className="mt-3 text-sm text-indigo-600 hover:text-indigo-700 font-medium">
              Erstes Video anlegen
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clientVideos.map(video => {
              const sc = STATUS_COLORS[video.status] || STATUS_COLORS['Entwurf'];
              return (
                <div
                  key={video.shareToken}
                  className="group bg-white rounded-2xl border border-gray-100 hover:border-indigo-200 hover:shadow-md transition-all cursor-pointer relative"
                  onClick={() => onOpen(video.shareToken)}
                >
                  <div className="h-36 rounded-t-2xl bg-gradient-to-br from-indigo-50 to-gray-50 flex items-center justify-center relative overflow-hidden">
                    <div className="absolute inset-0 opacity-5">
                      <div className="w-full h-full" style={{backgroundImage: 'radial-gradient(circle, #6366f1 1px, transparent 1px)', backgroundSize: '20px 20px'}} />
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-white/80 backdrop-blur flex items-center justify-center shadow-sm text-indigo-500">
                      <IconPlay size={18} />
                    </div>
                    {confirmDeleteVideo === video.shareToken ? (
                      <div className="absolute top-2 right-2 flex items-center gap-1 bg-white rounded-lg shadow-sm border border-red-100 px-2 py-1" onClick={e => e.stopPropagation()}>
                        <span className="text-xs text-red-600 font-medium">Löschen?</span>
                        <button className="text-xs text-red-600 hover:text-red-700 font-semibold" onClick={e => { e.stopPropagation(); handleDeleteVideo(video.shareToken); }}>Ja</button>
                        <button className="text-xs text-gray-400 hover:text-gray-600" onClick={e => { e.stopPropagation(); setConfirmDeleteVideo(null); }}>Nein</button>
                      </div>
                    ) : (
                      <button
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity w-7 h-7 bg-white/90 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-500 shadow-sm"
                        onClick={e => { e.stopPropagation(); setConfirmDeleteVideo(video.shareToken); }}
                      >
                        <IconTrash />
                      </button>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="text-sm font-semibold text-gray-900 leading-snug group-hover:text-indigo-700 transition-colors">{video.title}</h3>
                      <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1 ${sc.badge}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                        {video.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400">{video.createdAt}</p>
                  </div>
                  <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all translate-x-1 group-hover:translate-x-0">
                    <IconArrowRight />
                  </div>
                </div>
              );
            })}
            <button
              onClick={() => setAddingVideo(true)}
              className="h-full min-h-[160px] rounded-2xl border-2 border-dashed border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/30 flex flex-col items-center justify-center gap-2 text-gray-300 hover:text-indigo-400 transition-all"
            >
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M11 4V18M4 11H18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
              <span className="text-xs font-medium">Neues Video</span>
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Client list view ──
  return (
    <div className="max-w-5xl mx-auto px-6 py-10 h-full overflow-y-auto">
      {planTier === 'agency' && (
        <button
          onClick={onAgency}
          className="w-full mb-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-2xl p-4 flex items-center justify-between transition-all shadow-sm group"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 1l2 4h4l-3.5 2.5 1.5 4L8 9.5 4 11.5l1.5-4L2 5h4L8 1z" fill="white"/></svg>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold">Agentur-Tools</p>
              <p className="text-xs text-white/70">Kampagnenplanung · Social Media · KI-Strategie</p>
            </div>
          </div>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="opacity-70 group-hover:translate-x-0.5 transition-transform"><path d="M3 8H13M9 4L13 8L9 12" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
      )}

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Kunden</h1>
          <p className="text-sm text-gray-400 mt-0.5">{clients.length} Kunde{clients.length !== 1 ? 'n' : ''}</p>
        </div>
        <button
          onClick={() => setAddingClient(true)}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-xl transition-all shadow-sm"
        >
          <IconPlus />
          Neuer Kunde
        </button>
      </div>

      {addingClient && (
        <div className="mb-5 bg-white border border-indigo-200 rounded-2xl p-4 flex items-center gap-3 shadow-sm">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-500">
            <IconFolder size={18} />
          </div>
          <input
            autoFocus
            value={newClientName}
            onChange={e => setNewClientName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAddClient(); if (e.key === 'Escape') { setAddingClient(false); setNewClientName(''); } }}
            placeholder="Kundenname eingeben..."
            className="flex-1 text-sm font-medium text-gray-800 outline-none placeholder-gray-300"
          />
          <button onClick={handleAddClient} className="text-xs bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700 font-medium">Erstellen</button>
          <button onClick={() => { setAddingClient(false); setNewClientName(''); }} className="text-xs text-gray-400 hover:text-gray-600">Abbrechen</button>
        </div>
      )}

      {loadingClients ? (
        <div className="flex justify-center py-16">
          <svg className="animate-spin w-5 h-5 text-gray-300" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
        </div>
      ) : clients.length === 0 ? (
        <div className="text-center py-24">
          <div className="w-14 h-14 bg-gray-100 rounded-2xl mx-auto mb-4 flex items-center justify-center text-gray-300">
            <IconFolder size={24} />
          </div>
          <p className="text-gray-400 text-sm">Noch keine Kunden</p>
          <button onClick={() => setAddingClient(true)} className="mt-3 text-sm text-indigo-600 hover:text-indigo-700 font-medium">
            Ersten Kunden anlegen
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map(client => (
            <div
              key={client.id}
              className="group bg-white rounded-2xl border border-gray-100 hover:border-indigo-200 hover:shadow-md transition-all cursor-pointer relative"
              onClick={() => openClient(client)}
            >
              <div className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-11 h-11 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-500">
                    <IconFolder size={20} />
                  </div>
                  {confirmDeleteClient === client.id ? (
                    <div className="flex items-center gap-1 bg-white rounded-lg shadow border border-red-100 px-2 py-1" onClick={e => e.stopPropagation()}>
                      <span className="text-xs text-red-600 font-medium">Löschen?</span>
                      <button className="text-xs text-red-600 font-semibold hover:text-red-700" onClick={e => { e.stopPropagation(); handleDeleteClient(client.id); }}>Ja</button>
                      <button className="text-xs text-gray-400" onClick={e => { e.stopPropagation(); setConfirmDeleteClient(null); }}>Nein</button>
                    </div>
                  ) : (
                    <button
                      className="opacity-0 group-hover:opacity-100 w-7 h-7 bg-gray-50 hover:bg-red-50 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-500 transition-all"
                      onClick={e => { e.stopPropagation(); setConfirmDeleteClient(client.id); }}
                    >
                      <IconTrash />
                    </button>
                  )}
                </div>
                <h3 className="text-sm font-semibold text-gray-900 group-hover:text-indigo-700 transition-colors">{client.name}</h3>
                <p className="text-xs text-gray-400 mt-1">Ordner öffnen</p>
              </div>
              <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all translate-x-1 group-hover:translate-x-0">
                <IconArrowRight />
              </div>
            </div>
          ))}
          <button
            onClick={() => setAddingClient(true)}
            className="h-full min-h-[120px] rounded-2xl border-2 border-dashed border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/30 flex flex-col items-center justify-center gap-2 text-gray-300 hover:text-indigo-400 transition-all"
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M11 4V18M4 11H18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            <span className="text-xs font-medium">Neuer Kunde</span>
          </button>
        </div>
      )}
    </div>
  );
}
